import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  PlayCircle, ChevronLeft, ChevronRight, X, Sparkles, ShieldCheck,
  Handshake, ShoppingBag, Network, Check, Lock, UserRound, Info, Pin, PinOff
} from "lucide-react";

const CHAPTERS = [
  {
    id: "why",
    title: "From static profiles to trusted workflows",
    eyebrow: "01 · Why this matters",
    purpose: "Why static profiles, shop pages and forms aren't enough once young talent, sponsors, parents and evidence are involved.",
    Icon: Sparkles,
    points: [
      "Existing club sites already show profiles, sponsors, shops and events.",
      "The hard part is proving claims, managing permissions and evidencing what happened.",
      "AI can structure the work — but it must be bounded.",
      "KATLAS adds the operational trust layer: role, permission, approval, receipt.",
    ],
    reduces: ["Unverifiable profile claims", "Informal sponsor introductions", "Scattered admin", "Accidental over-sharing"],
    human: ["Sponsorship introductions", "Safeguarding-sensitive actions", "Junior permissions", "Final approvals"],
    tagline: "Most club sites publish profiles, products and opportunities. ClubCierge turns key actions into accountable, real-time governed activity.",
    cta: { label: "See the KATLAS / CAR proof layer", to: "/katlas-capability" },
    discovery: "Where do you currently rely most on trust or manual checking?",
    presenter: {
      say: "When a sponsor introduction, parent approval or support claim happens, KATLAS signs the receipt: who approved what, when, what was shared, and what was withheld.",
      click: "Walkthrough chapter 01 CTA → KATLAS capability page",
      ask: "Where does manual trust hurt you most today?",
    },
  },
  {
    id: "how",
    title: "Member-controlled profiles, not more central admin",
    eyebrow: "02 · How",
    purpose: "Each member holds a controlled digital club profile / wallet — proving the right thing without exposing more data than needed.",
    Icon: ShieldCheck,
    points: [
      "Members, drivers, parents, sponsors, officers and suppliers can hold controlled profiles.",
      "Wallets prove role, permission and approved evidence.",
      "AI does not get open access to private data.",
      "KATLAS records the governed action — not everyone's private data.",
    ],
    reduces: ["Manual checking", "Duplicated documents", "Unnecessary data exposure", "Unclear permissions"],
    human: ["What the member allows to share", "What parents approve for juniors", "What officers approve for the club"],
    tagline: "Self-sovereign profiles let the right role prove the right fact without exposing everything. Members, parents, sponsors and officers use controlled profiles; the system proves only the facts needed for the action, while private or withheld evidence stays under the right role's control.",
    cta: { label: "Open Marcus's profile", to: "/drivers/d-marcus" },
    discovery: "What information should members control themselves?",
    presenter: {
      say: "Notice the wallet card on the offers page — proofs available, nothing more.",
      click: "Driver profile → Sponsor pack tab → Eligible support & kit",
      ask: "What member data are you currently storing that you don't really need to?",
    },
  },
  {
    id: "intro",
    title: "Drivers prove performance. Sponsors prove intent.",
    eyebrow: "03 · Sponsor introduction workflow",
    purpose: "The main happy path: Marcus Reid × Pole Position. AI suggests, an officer approves, and the receipt holds the evidence.",
    Icon: Handshake,
    points: [
      "AI suggests alignment from performance, intent and constraints.",
      "Deterministic flags and permissions bound the match.",
      "Club Secretary approves the introduction.",
      "Receipt records what was checked, shared and withheld.",
    ],
    reduces: ["Cold or informal sponsor introductions", "Sponsor mismatch", "Claims without evidence", "Confusion about what was shared"],
    human: ["Whether the introduction is appropriate", "What is shared", "What is withheld", "Final approval"],
    tagline: "AI suggests the match. Wallets prove the permitted facts. KATLAS governs the approval. A human officer decides.",
    cta: { label: "Review Marcus × Pole Position", to: "/inbox?id=MX-INIT-001" },
    discovery: "What evidence would make sponsors take a young driver more seriously?",
    presenter: {
      say: "This is where the platform turns a sponsor lead into a governed introduction.",
      click: "Marcus → Pole Position → Inbox → Approve → Receipt",
      ask: "Would this help you manage sponsor introductions more consistently?",
    },
  },
  {
    id: "shop",
    title: "Who can shop for what — safely",
    eyebrow: "04 · Bounded shop & supplier services",
    purpose: "Secondary extension: shop access bounded by role, age band, sponsor introduction, parental permission and supplier terms.",
    Icon: ShoppingBag,
    points: [
      "Shop / service access is gated by role, age band, permissions and approvals.",
      "Parent / guardian approval applies for junior personalisation or contact details.",
      "Suppliers prove suitability before offers can be claimed.",
      "KATLAS governs the permissioned claim — no payment is handled in this POC.",
    ],
    reduces: ["Inappropriate access by juniors", "Supplier offers without eligibility checks", "Manual approval chasing", "Unnecessary sharing of junior data"],
    human: ["Parent / guardian approval", "Officer approval", "Supplier verification", "Sensitive sharing"],
    tagline: "Members prove eligibility. Parents approve where needed. Suppliers prove suitability. KATLAS records the governed interaction.",
    cta: { label: "View eligible support & kit", to: "/drivers/d-marcus/offers" },
    discovery: "Which shop, supplier or support offers need eligibility or parental controls?",
    presenter: {
      say: "Same trust pattern — extended from sponsors into the club shop and partner services.",
      click: "Marcus profile → Eligible support & kit → Track Coaching Voucher (officer + parent)",
      ask: "Where would a parental gate matter most to your members?",
    },
  },
  {
    id: "scale",
    title: "Start with one club. Scale the trust pattern across many.",
    eyebrow: "05 · Pathway to scale",
    purpose: "The same pattern extends from motorsport to other clubs, sports, suppliers, insurers and governing bodies — without making this demo generic.",
    Icon: Network,
    points: [
      "Stage 1 — Site enhancement: AI workflows + wallet-backed permissions on an existing club site.",
      "Stage 2 — Trusted introductions: governed sponsor / supporter matching with receipts.",
      "Stage 3 — Supplier & shop services: bounded access to kit, offers, coaching, partner services.",
      "Stage 4 — Insurance & risk evidence: receipts and controlled records for insurers / committees.",
      "Stage 5 — Multi-club / multi-sport pattern: football, hockey, rugby, tennis, athletics, swimming…",
    ],
    reduces: ["Rebuilding from scratch", "Isolated club admin", "Unverifiable ecosystem claims", "Supplier / sponsor risk"],
    human: ["Local club policy", "Sport-specific rules", "Safeguarding decisions", "Commercial partnerships"],
    tagline: "The demo starts with Marcus and Pole Position. The pattern scales to members, partners and clubs.",
    cta: { label: "Back to landing", to: "/" },
    discovery: "Which workflow would you scale first — sponsor matching, supplier offers, insurance evidence or junior permissions?",
    presenter: {
      say: "Motorsport is the first visible example. The trust pattern is reusable.",
      click: "Stay on this chapter — talk through the five stages.",
      ask: "Where would you want to start: one club, or a federation view?",
    },
  },
];

export default function Walkthrough({ open, onOpenChange, initialChapter = 0 }) {
  const [idx, setIdx] = useState(initialChapter);
  const [presenter, setPresenter] = useState(false);
  const [pinned, setPinned] = useState(false);
  const chapter = CHAPTERS[idx];
  const Icon = chapter.Icon;

  const go = (i) => setIdx(Math.max(0, Math.min(CHAPTERS.length - 1, i)));
  const onCta = () => { if (!pinned) onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!pinned}>
      <DialogContent
        className={
          pinned
            ? "fixed top-auto bottom-6 right-6 left-auto translate-x-0 translate-y-0 max-w-md p-0 gap-0 overflow-hidden shadow-2xl border-2 border-[#F25E1D]/40"
            : "max-w-4xl p-0 gap-0 overflow-hidden"
        }
        overlayClassName={pinned ? "bg-transparent pointer-events-none" : undefined}
        onInteractOutside={(e) => { if (pinned) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (pinned) e.preventDefault(); }}
        data-testid="walkthrough-dialog"
        data-pinned={pinned ? "true" : "false"}
      >
        <DialogTitle className="sr-only">{chapter.title}</DialogTitle>
        <DialogDescription className="sr-only">{chapter.purpose}</DialogDescription>
        <div className={pinned ? "min-h-0" : "grid md:grid-cols-[220px_1fr] min-h-[560px]"}>
          {/* Chapter rail (hidden in pinned mode for compactness) */}
          {!pinned && (
          <aside className="bg-[#131318] text-white p-5 hidden md:flex flex-col gap-1">
            <div className="tracking-eyebrow text-[#A8A8B2]">Walkthrough</div>
            <div className="text-[11px] text-[#7C7C86] mb-3 leading-relaxed">Pick a chapter. Skip freely.</div>
            <ol className="space-y-1 flex-1">
              {CHAPTERS.map((c, i) => {
                const C = c.Icon;
                const active = i === idx;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => go(i)}
                      data-testid={`walkthrough-chapter-${c.id}`}
                      className={`w-full text-left flex items-start gap-2 px-2 py-2 rounded-md transition-colors ${
                        active ? "bg-[#26262E] text-white" : "text-[#B5B5BE] hover:bg-[#1F1F25]"
                      }`}
                    >
                      <C className={`h-4 w-4 mt-0.5 shrink-0 ${active ? "text-[#F25E1D]" : ""}`} />
                      <span className="min-w-0">
                        <span className="block font-mono-ui text-[10px] tracking-wider opacity-70">0{i + 1}</span>
                        <span className="block text-[12px] leading-snug">{c.title}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="flex items-center justify-between border-t border-[#26262E] pt-3 mt-3 gap-2">
              <button
                onClick={() => setPresenter((p) => !p)}
                className="text-[11px] text-[#A8A8B2] hover:text-white inline-flex items-center gap-1"
                data-testid="presenter-toggle"
              >
                <Info className="h-3 w-3" /> Presenter {presenter ? "on" : "off"}
              </button>
              <button
                onClick={() => setPinned(true)}
                className="text-[11px] text-[#A8A8B2] hover:text-white inline-flex items-center gap-1"
                data-testid="pin-toggle"
                title="Keep the walkthrough visible while you click around"
              >
                <Pin className="h-3 w-3" /> Pin
              </button>
            </div>
          </aside>
          )}

          {/* Chapter body */}
          <section className={`p-6 sm:p-8 flex flex-col gap-4 ${pinned ? "max-h-[70vh]" : "max-h-[80vh]"} overflow-y-auto`} data-testid="walkthrough-body">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="tracking-eyebrow text-muted-foreground">{chapter.eyebrow}</div>
                <h2 className={`${pinned ? "text-lg" : "text-2xl sm:text-3xl"} font-semibold mt-1 leading-tight`}>{chapter.title}</h2>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <div className="h-9 w-9 rounded-md grid place-items-center bg-[#FAFAFB] border border-border">
                  <Icon className="h-4 w-4 text-[#F25E1D]" />
                </div>
                {pinned && (
                  <button
                    onClick={() => setPinned(false)}
                    className="h-9 w-9 grid place-items-center rounded-md border border-border hover:bg-secondary"
                    data-testid="unpin-toggle"
                    title="Unpin walkthrough"
                  >
                    <PinOff className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">{chapter.purpose}</p>

            {!pinned && (
              <ul className="space-y-1.5 text-[14px]">
                {chapter.points.map((p, i) => (
                  <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-1 text-[var(--rag-green-text)] shrink-0" /><span>{p}</span></li>
                ))}
              </ul>
            )}

            {!pinned && (
              <div className="grid sm:grid-cols-2 gap-3 mt-1">
                <div className="rounded-md border border-border p-3 bg-[#FAFAFB]">
                  <div className="tracking-eyebrow text-muted-foreground flex items-center gap-1">
                    <Lock className="h-3 w-3" /> What this reduces
                  </div>
                  <ul className="text-[12px] mt-1.5 space-y-0.5 text-muted-foreground">
                    {chapter.reduces.map((r) => <li key={r}>· {r}</li>)}
                  </ul>
                </div>
                <div className="rounded-md border border-border p-3 bg-white">
                  <div className="tracking-eyebrow text-muted-foreground flex items-center gap-1">
                    <UserRound className="h-3 w-3" /> Stays human-controlled
                  </div>
                  <ul className="text-[12px] mt-1.5 space-y-0.5">
                    {chapter.human.map((r) => <li key={r}>· {r}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <blockquote className="text-[13px] italic text-muted-foreground border-l-2 border-[#F25E1D] pl-3 leading-relaxed">
              “{chapter.tagline}”
            </blockquote>

            {presenter && (
              <div className="rounded-md border border-dashed border-[#F25E1D]/40 bg-[#FFF7F2] p-3 text-[12px] space-y-1.5" data-testid="presenter-notes">
                <div><b>Say:</b> {chapter.presenter.say}</div>
                <div><b>Click:</b> <span className="font-mono-ui">{chapter.presenter.click}</span></div>
                <div><b>Ask:</b> {chapter.presenter.ask}</div>
              </div>
            )}

            {!pinned && (
              <div className="text-[12px] text-muted-foreground border-t border-border pt-3 mt-auto">
                <b>Discovery question:</b> {chapter.discovery}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => go(idx - 1)} data-testid="walkthrough-prev">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button variant="ghost" size="sm" disabled={idx >= CHAPTERS.length - 1} onClick={() => go(idx + 1)} data-testid="walkthrough-next">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="walkthrough-close">
                  <X className="h-3.5 w-3.5 mr-1" /> Close
                </Button>
                <Link to={chapter.cta.to} onClick={onCta} data-testid={`walkthrough-cta-${chapter.id}`}>
                  <Button size="sm">{chapter.cta.label}</Button>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StartWalkthroughButton({ variant = "default", size = "lg", label = "Start walkthrough" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)} data-testid="start-walkthrough-btn">
        <PlayCircle className="h-4 w-4 mr-1.5" /> {label}
      </Button>
      <Walkthrough open={open} onOpenChange={setOpen} />
    </>
  );
}
